/**
 * WHICH PAGE A SAVED VIEW BELONGS TO.
 *
 * A view is per (report, tab), and as of 24 Sep 2026 the platform has ONE name
 * for a tab on both sides: `db/platform/037_saved_views.sql` reconciled
 * `plat.Report_Page_MBI` so every slug there is the client's own `TabId`. So there
 * is no mapping table here and no translation anywhere — the page slug a view
 * is saved against IS the tab id.
 *
 * That was worth doing rather than inventing a client-side key beside it.
 * `plat.Report_Page_MBI` had been seeded since the Power BI port, read by nothing,
 * and had quietly drifted: it still claimed Receivables had a
 * "Decomposition (Salesperson)" page that had not existed since the rebuild. A
 * second vocabulary alongside a stale first one is how a third one starts.
 *
 * THE TAB IDS COLLIDE ACROSS REPORTS and that is fine, because the report is
 * always part of the key: `tree` is a tab of both Receivables and Sales, and
 * `trend` of both Sales and Material Planning. `UQ (Report_Id, Slug)` on the
 * table is what makes that safe rather than a convention.
 *
 * `tools/gates/assert-page-slugs.mjs` fails the build if this list and the
 * database's ever disagree again.
 */

export type ReportSlug = 'receivables' | 'sales' | 'material-planning' | 'inventory-aging' | 'statement-of-account'

/**
 * Every tab of every report, in the order the report shows them.
 *
 * Declared here rather than derived from each page's own `TABS` array because
 * the gate has to read it statically, and because this is the list the database
 * is checked against — one written-down truth, checked both ways.
 */
export const REPORT_TABS: Record<ReportSlug, readonly string[]> = {
  receivables: ['dashboard', 'transactions', 'dso', 'tree', 'weeks', 'trends', 'review'],
  sales: ['explorer', 'leaderboards', 'trend', 'tree', 'lines'],
  'material-planning': [
    'overview', 'company', 'stock', 'purchase', 'shipments',
    'arriving', 'late', 'items', 'performance', 'trend',
  ],
  // Seeded by its own registration (039), not by 037: the gate reads both.
  'inventory-aging': ['aging', 'expiry', 'lots'],
  // One screen, seeded by 042.
  'statement-of-account': ['statement'],
}

/**
 * The grids a tab draws, so a view can carry their column layouts.
 *
 * A tab with no entry has no grid — Receivables' Tree View and Trends are
 * charts, and a view of those carries filters and pickers and nothing else.
 *
 * `mp-shipments` DELIBERATELY APPEARS THREE TIMES. Material Planning's
 * Shipment Tracker, Arriving Soon and Late Shipments are one grid under three
 * filters (`SHIPMENT_VIEW` in MaterialPlanningPage), so a column arrangement
 * saved on one is the same grid's arrangement on the others. That follows from
 * layouts being per grid and views being per tab, and it is written down here
 * because it will otherwise be reported as a bug.
 */
export const TAB_GRIDS: Record<ReportSlug, Record<string, readonly string[]>> = {
  receivables: {
    dashboard: ['receivables-matrix'],
    transactions: ['receivables-transactions'],
    dso: ['receivables-dso'],
    weeks: ['receivables-weeks'],
  },
  sales: {
    explorer: ['sales-explorer'],
    lines: ['sales-invoice-lines'],
  },
  'material-planning': {
    company: ['mp-company'],
    stock: ['mp-stock'],
    purchase: ['mp-purchase'],
    shipments: ['mp-shipments'],
    arriving: ['mp-shipments'],
    late: ['mp-shipments'],
    items: ['mp-items'],
    performance: ['mp-performance'],
  },
  /* Expiry and Lots are one procedure behind different windows, but NOT one
     grid: Lots carries every attribute of a lot and Expiry the handful an
     expiry list is read for, so each keeps its own arrangement. */
  /* Aging draws ONE of two grids — the drill-down or the list — and a view
     carries both arrangements, so switching between them keeps each. */
  'inventory-aging': {
    aging: ['inv-aging-drill', 'inv-aging'],
    expiry: ['inv-expiry'],
    lots: ['inv-lots'],
  },
  /* The statement is a document, not a grid a reader arranges: its columns are
     the ones the customer will receive, in that order. */
  'statement-of-account': {},
}

/** The grids on one tab, or none. */
export function gridsOnTab(report: ReportSlug, tab: string): readonly string[] {
  return TAB_GRIDS[report][tab] ?? []
}
